import { useState, useCallback, useEffect, useRef } from "react";
import { useDebounce } from "@ui/utils/useDebounce";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getAllAccessPoints, getAccessPointsStatistics, } from "@reducers/access-points/thunk/access-point-thunk";
import { setGlobalSearch, setPageNumber, setPageSize, setSorting, updateFilters, } from "@reducers/access-points/slice/access-point-slice";
import { userService } from "@utility/user-service";
import { getLatestApActivityIso } from "@utility/access-point-monitoring";
/** Last grid column: max UTC across `endPointCommunicationMonitoring` (lastCommunicationInterval + per-route fields). */
function lastUpdatedAtFromAp(ap) {
    return getLatestApActivityIso(ap);
}
/** Filter-dialog fields that are id lists, and the AccessPointFilters key each one maps to. */
const LIST_FILTERS = {
    clientIds: "clientIds",
    clientLocationIds: "clientLocationIds",
    apCategoryIds: "apCategoryIds",
};
const EMPTY_FILTERS = {
    nameId: "",
    lastUpdate: "",
    clientIds: [],
    clientLocationIds: [],
    apCategoryIds: [],
};
export const useDashboardFilter = () => {
    var _a, _b, _c;
    const dispatch = useAppDispatch();
    const { accessPoints, loading, paginationInfo, filters: storeFilters, currentPageNumber, currentPageSize, sortColumn, sortDirection, statistics, statisticsLoading, } = useAppSelector((state) => state.accessPoint);
    const [searchTerm, setSearchTerm] = useState(storeFilters.globalSearch || "");
    const [filters, setFilters] = useState({
        nameId: storeFilters.accessPointName || "",
        lastUpdate: storeFilters.updatedAt || "",
        clientIds: (_a = storeFilters.clientIds) !== null && _a !== void 0 ? _a : [],
        clientLocationIds: (_b = storeFilters.clientLocationIds) !== null && _b !== void 0 ? _b : [],
        apCategoryIds: (_c = storeFilters.apCategoryIds) !== null && _c !== void 0 ? _c : [],
    });
    // The tab lives in the store so it survives navigating away and back. Three
    // distinct values matter: `undefined` = never chosen (default Active), `null`
    // = the All tab, `true`/`false` = Active/Inactive. `??` would collapse `null`
    // into the default and silently drop the user back to Active.
    const activeFilter = storeFilters.isActive === undefined ? true : storeFilters.isActive;
    const selectedCard = activeFilter === null
        ? "all-devices"
        : activeFilter
            ? "active-devices"
            : "inactive-devices";
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const prevQueryRef = useRef("");
    const abortRef = useRef(null);
    const cleanFilters = useCallback((filters) => {
        const cleaned = {};
        if (filters.nameId &&
            typeof filters.nameId === "string" &&
            filters.nameId.trim() !== "") {
            cleaned.accessPointName = filters.nameId.trim();
        }
        if (filters.lastUpdate &&
            typeof filters.lastUpdate === "string" &&
            filters.lastUpdate.trim() !== "") {
            cleaned.updatedAt = filters.lastUpdate;
        }
        // Client / location / category: id lists; empty lists are sent as "no filter" (undefined), so the store
        // merge clears a previously chosen value.
        Object.entries(LIST_FILTERS).forEach(([field, key]) => {
            const value = filters[field];
            const ids = (Array.isArray(value) ? value : value ? [value] : []).filter((id) => id);
            cleaned[key] = ids.length > 0 ? ids : undefined;
        });
        return cleaned;
    }, []);
    const hasFetchedStatistics = useRef(false);
    useEffect(() => {
        if (!hasFetchedStatistics.current) {
            hasFetchedStatistics.current = true;
            if (userService.hasRole("Super Admin")) {
                dispatch(getAccessPointsStatistics());
            }
        }
    }, [dispatch]);
    useEffect(() => {
        var _a;
        const gridFilters = cleanFilters(filters);
        const combinedFilters = Object.assign({ globalSearch: debouncedSearchTerm || undefined }, gridFilters);
        if (activeFilter !== null) {
            combinedFilters.isActive = activeFilter;
        }
        const queryKey = JSON.stringify({
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            sortColumn: sortColumn || null,
            sortDirection: sortDirection || null,
            filters: combinedFilters,
        });
        if (queryKey === prevQueryRef.current)
            return;
        prevQueryRef.current = queryKey;
        // Cancel any superseded in-flight request so stale responses never overwrite
        // the current tab's data.
        (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.call(abortRef);
        abortRef.current = null;
        const thunkPromise = dispatch(getAllAccessPoints({
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            sortColumn: sortColumn || undefined,
            sortDirection: sortDirection || undefined,
            filters: combinedFilters,
        }));
        abortRef.current = () => thunkPromise.abort();
    }, [
        dispatch,
        debouncedSearchTerm,
        currentPageNumber,
        currentPageSize,
        sortColumn,
        sortDirection,
        activeFilter,
        filters,
        cleanFilters,
    ]);
    // Update store search when debounced value changes
    useEffect(() => {
        dispatch(setGlobalSearch(debouncedSearchTerm || undefined));
    }, [dispatch, debouncedSearchTerm]);
    const handleFilterChange = useCallback((newFilters) => {
        setFilters((prev) => {
            const updated = Object.assign({}, prev);
            Object.entries(newFilters).forEach(([key, value]) => {
                if (value !== undefined) {
                    updated[key] = value;
                }
                else {
                    delete updated[key];
                }
            });
            return updated;
        });
        const mergedFilters = Object.assign(Object.assign({}, filters), newFilters);
        const filteredFilters = Object.fromEntries(Object.entries(mergedFilters).filter(([_, v]) => v !== undefined));
        const cleanedFilters = cleanFilters(filteredFilters);
        dispatch(updateFilters(cleanedFilters));
        dispatch(setPageNumber(1));
    }, [dispatch, cleanFilters, filters]);
    const handleSearchChange = useCallback((event) => {
        setSearchTerm(event.target.value);
    }, []);
    const handleToggleStatus = useCallback((device) => { }, []);
    const handlePageChange = useCallback((page) => {
        dispatch(setPageNumber(page));
    }, [dispatch]);
    const handlePageSizeChange = useCallback((size) => {
        dispatch(setPageSize(size));
    }, [dispatch]);
    const handleSortChange = useCallback((column, direction) => {
        dispatch(setSorting({ sortColumn: column, sortDirection: direction }));
    }, [dispatch]);
    // Handle active/inactive filter from card click
    const handleActiveFilterChange = useCallback((isActive) => {
        dispatch(setPageNumber(1));
        dispatch(updateFilters({ isActive }));
    }, [dispatch]);
    // Handle clear filters
    const handleClearFilters = useCallback(() => {
        setFilters(EMPTY_FILTERS);
        setSearchTerm("");
        dispatch(setPageNumber(1));
        // `updateFilters` merges, so isActive has to be reset explicitly — an empty
        // payload would leave the previous tab in place.
        dispatch(updateFilters({
            isActive: null,
            accessPointName: undefined,
            updatedAt: undefined,
            clientIds: undefined,
            clientLocationIds: undefined,
            apCategoryIds: undefined,
        }));
        dispatch(setGlobalSearch(undefined));
    }, [dispatch]);
    const devices = (accessPoints || []).map((ap) => {
        var _a;
        return ({
            id: ap.id || ap.accessPointUID,
            name: ap.accessPointName || ap.accessPointUID,
            deviceId: ap.accessPointUID,
            status: ap.isActive ? "active" : "inactive",
            lastUpdate: lastUpdatedAtFromAp(ap),
            location: ap.location,
            hasMaintenanceRequest: ap.hasMaintenanceRequest,
            maintenanceRequestCount: ap.maintenanceRequestCount,
            nodeCount: (_a = ap.nodeCount) !== null && _a !== void 0 ? _a : 0,
        });
    });
    return {
        devices,
        loading,
        searchTerm,
        filters,
        paginationInfo,
        currentPageNumber,
        currentPageSize,
        statistics,
        statisticsLoading,
        activeFilter,
        selectedCard,
        handleFilterChange,
        handleSearchChange,
        handleToggleStatus,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        handleActiveFilterChange,
        handleClearFilters,
    };
};
